import React from 'react';
import gettext from 'js-utils/gettext';
import classnames from 'classnames';
import { logCheckpointViaAjax } from 'js-utils/janus';
import { getFormattedTimezone, getFormattedDateTime } from 'js-utils/datetime';
import {
    ANCHOR_LINK,
    DIVIDER,
} from 'eventbrite_design_system/navListItem/constants';
import { isMediumDown } from 'eventbrite_design_system/utils/mediaQueries';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import {
    getLogoInfo,
    getUserMenuInfo as getDefaultUserMenu,
} from '@eb/site-header';
import { getUserMenuInfo as getOrganizationUserMenuInfo } from '../organizations';
import {
    ORG_LOGO_BK_COLOR,
    ORG_ICON_COLOR as ORG_ICON_GRAY_COLOR,
    ORG_ICON_GREEN_COLOR,
    PUBLISH_EVENT_INTENT,
    SCHEDULE_PUBLISH_EVENT_INTENT,
    CANCEL_SCHEDULE_EVENT_INTENT,
    EVENT_STATUS_BUTTON_UNPUBLISHED,
    EVENT_STATUS_BUTTON_PUBLISH_SCHEDULED,
    EVENT_STATUS_BUTTON_PUBLISHED,
    EVENT_STATUS_BUTTON_CANCELLED,
    EVENT_STATUS_BUTTON_ENDED,
    HEADER_LABELS,
    EVENT_SALES_STATUS_ICON_AND_COLOR_MAP,
    SALES_STATUS_CUSTOM_VALUE,
    EVENT_SALES_STATUS_CUSTOM_MESSAGE,
    EVENT_SALES_STATUS_DEFAULT_VALUES,
    EVENT_SALES_STATUS_MESSAGE_CODE_TICKETS_AT_THE_DOOR,
    DEFAULT_LANGUAGE,
    GA_TRACKING_EVENT_CATEGORY,
    ACTION_EVENT_PREVIEW,
} from '../constants';
import {
    eventIsPublishable,
    eventIsUnpublishable,
    eventIsCanceled,
    eventIsEnded,
    eventEndDateHasPassed,
} from '../event';

import EyeOnChunkySvg from 'eventbrite_design_system/iconography/icons/EyeOnChunky';
import PublishChunkySvg from 'eventbrite_design_system/iconography/icons/PublishChunky';
import CalendarStatusChunkySvg from 'eventbrite_design_system/iconography/icons/CalendarStatusChunky';
import StatusDotChunkySvg from 'eventbrite_design_system/iconography/icons/StatusDotChunky';
import ClockFillSvg from 'eventbrite_design_system/iconography/icons/ClockFill';
import CalendarCheckMarkChunkySvg from 'eventbrite_design_system/iconography/icons/CalendarCheckMarkChunky';
import CalendarOffChunkySvg from 'eventbrite_design_system/iconography/icons/CalendarOffChunky';

export const getOrganizerLogoInfo = ({
    url,
    requestPath,
    logoOptions = {},
    iconColor = ORG_LOGO_BK_COLOR,
}) =>
    getLogoInfo(url, requestPath, {
        iconColor,
        ...logoOptions,
    });

const ActionText = ({ text, className, ...otherProps }) => (
    <span className={className} {...otherProps}>
        {text}
    </span>
);

const getDateFormated = (utc, timezone, locale) => {
    if (isMediumDown()) {
        return getFormattedDateTime(
            utc,
            false,
            timezone,
            locale,
            'MMM D [•] LT',
        );
    }

    return getFormattedDateTime(utc, false, timezone, locale, 'MMM D');
};

/* This function is exported for testing purposes */
export const getLabelAndIconToDisplay = (
    {
        salesStatusCode,
        isNotYetOnSale,
        isOnSale,
        isNotYetOnSaleAccessCoded,
        salesStatusMessage,
        salesStatusMessageType,
        salesStatusMessageCode,
        salesStartDate,
    } = {},
    locale = DEFAULT_LANGUAGE,
) => {
    if (!salesStatusCode) {
        return {};
    }

    if (isOnSale || isNotYetOnSaleAccessCoded) {
        // for on sale or not yet on sale access coded status, we will show the status message associated with
        // the status code
        return EVENT_SALES_STATUS_ICON_AND_COLOR_MAP[salesStatusCode];
    } else if (
        salesStatusMessageCode ===
        EVENT_SALES_STATUS_MESSAGE_CODE_TICKETS_AT_THE_DOOR
    ) {
        // If the event is sales ended bacause will be sold at the door ,it will display At The Door
        return EVENT_SALES_STATUS_ICON_AND_COLOR_MAP[salesStatusMessageCode];
    }

    let mapOption = EVENT_SALES_STATUS_DEFAULT_VALUES;
    let salesStartDateFormatted = null;
    let eventStatusLabel = null;

    if (isNotYetOnSale) {
        mapOption = salesStatusCode;
        if (salesStatusMessageType !== SALES_STATUS_CUSTOM_VALUE) {
            salesStartDateFormatted = getDateFormated(
                salesStartDate.utc,
                salesStartDate.timezone,
                locale,
            );
        }
    }

    const {
        eventStatusIconColor,
        eventStatusNarrowIconType,
        eventStatusNarrowIconColor,
    } = EVENT_SALES_STATUS_ICON_AND_COLOR_MAP[mapOption];

    if (salesStartDateFormatted) {
        // If salesStartDateFormatted is not null it will concat 'On Sale '
        // with the formatted date to display e.g.: On Sale Feb 9
        eventStatusLabel = gettext('On Sale %(date)s', {
            date: salesStartDateFormatted,
        });
    } else if (salesStatusMessageType === SALES_STATUS_CUSTOM_VALUE) {
        // This will display Custom text on the Header
        eventStatusLabel = EVENT_SALES_STATUS_CUSTOM_MESSAGE;
    } else {
        // This will display the default status message e.g.; Sold out, Cancelled, Sales Ended, etc.
        eventStatusLabel = salesStatusMessage;
    }
    return {
        eventStatusLabel,
        eventStatusIconColor,
        eventStatusNarrowIconType,
        eventStatusNarrowIconColor,
    };
};

export const getEventStatusButtonType = (eventData) => {
    if (!eventData) {
        return null;
    }

    if (eventIsCanceled(eventData)) {
        return EVENT_STATUS_BUTTON_CANCELLED;
    } else if (
        eventIsEnded(eventData) ||
        eventEndDateHasPassed(eventData.end, eventData.timezone)
    ) {
        return EVENT_STATUS_BUTTON_ENDED;
    } else if (eventIsUnpublishable(eventData)) {
        return EVENT_STATUS_BUTTON_PUBLISHED;
    } else if (eventData.hasSchedulePublish) {
        return EVENT_STATUS_BUTTON_PUBLISH_SCHEDULED;
    } else if (eventIsPublishable(eventData)) {
        return EVENT_STATUS_BUTTON_UNPUBLISHED;
    }

    return null;
};

export const getViewEventLinks = (serverUrl, eventId) => [
    {
        url: `${serverUrl}/e/${eventId}/`,
        target: '_blank',
        narrowIconType: <EyeOnChunkySvg />,
        narrowIconColor: ORG_ICON_GRAY_COLOR,
        content: HEADER_LABELS.VIEW_LABEL,
        onClick: () => logCheckpointViaAjax('view_event_listing', serverUrl),
    },
];

export const getPreviewEventLinks = (serverUrl, eventId) => [
    {
        url: `${serverUrl}/preview?eid=${eventId}/`,
        target: '_blank',
        narrowIconType: <EyeOnChunkySvg />,
        narrowIconColor: ORG_ICON_GRAY_COLOR,
        content: HEADER_LABELS.PREVIEW_LABEL,
        onClick: () =>
            trackAnalyticsEvent({
                action: ACTION_EVENT_PREVIEW,
                category: GA_TRACKING_EVENT_CATEGORY,
            }),
    },
];

export const getUnpublishedEventActionsMenu = (
    serverUrl,
    isScheduleEventPublishEnabled,
) => {
    const publishEventButtonText = isScheduleEventPublishEnabled
        ? HEADER_LABELS.PUBLIC_NOW_LABEL
        : HEADER_LABELS.PUBLISH_EVENT_LABEL;
    const publishEventButtonClass = classnames({
        'eds-text-color--ui-600': isScheduleEventPublishEnabled,
    });
    let textItems = [
        {
            content: (
                <ActionText
                    text={publishEventButtonText}
                    className={publishEventButtonClass}
                    data-automation="coyote-publish-button"
                />
            ),
            type: ANCHOR_LINK,
            value: PUBLISH_EVENT_INTENT,
        },
    ];

    if (isScheduleEventPublishEnabled) {
        textItems = [
            ...textItems,
            {
                content: (
                    <ActionText
                        text={HEADER_LABELS.SCHEDULE_PUBLISH_EVENT_LABEL}
                        className="eds-text-color--ui-600"
                        data-automation="coyote-schedule-publish-button"
                    />
                ),
                type: ANCHOR_LINK,
                value: SCHEDULE_PUBLISH_EVENT_INTENT,
            },
        ];
    }

    return {
        textItems,
        label: HEADER_LABELS.PUBLISH_EVENT_LABEL,
        narrowIconType: <PublishChunkySvg />,
        narrowIconColor: ORG_ICON_GRAY_COLOR,
        fallbackUrl: serverUrl,
    };
};

export const getPublishedEventActionsMenu = ({
    serverUrl,
    onUnpublishClick,
    onChangeSalesStatusClick,
    manageNewEventStatusFeatureEnabled,
    eventSalesStatus,
    hasPermissionToManageEventSalesStatus,
    locale,
}) => {
    const newEventStatusLabelIcons = getLabelAndIconToDisplay(
        eventSalesStatus,
        locale,
    );
    const {
        eventStatusLabel = HEADER_LABELS.PUBLISHED_LABEL,
        eventStatusIconColor = ORG_ICON_GREEN_COLOR,
        eventStatusNarrowIconType = <CalendarStatusChunkySvg />,
        eventStatusNarrowIconColor = ORG_ICON_GRAY_COLOR,
    } = newEventStatusLabelIcons;
    let navItems = [
        {
            type: ANCHOR_LINK,
            content: HEADER_LABELS.UNPUBLISH_EVENT_LABEL,

            onClick: (e) => {
                e.preventDefault();
                onUnpublishClick();
            },
        },
    ];

    if (manageNewEventStatusFeatureEnabled) {
        navItems = [
            {
                type: ANCHOR_LINK,
                content: HEADER_LABELS.CHANGE_STATUS_LABEL,
                isDisabled: !hasPermissionToManageEventSalesStatus,
                onClick: (e) => {
                    e.preventDefault();
                    onChangeSalesStatusClick();
                },
            },
            ...navItems,
        ];
    }

    if (isMediumDown()) {
        navItems = [
            {
                content: (
                    <ActionText
                        text={eventStatusLabel}
                        className="eds-text-color--ui-500"
                    />
                ),
                type: ANCHOR_LINK,
                isDisabled: true,
            },
            {
                type: DIVIDER,
            },
            ...navItems,
        ];
    }

    return {
        navItems,
        iconType: <StatusDotChunkySvg />,
        narrowIconType: eventStatusNarrowIconType,
        narrowIconColor: eventStatusNarrowIconColor,
        iconColor: eventStatusIconColor,
        label: eventStatusLabel,
        fallbackUrl: serverUrl,
        hideDropIconWhenNarrow: true,
    };
};

export const getSchedulePublishEventActionsMenu = (
    serverUrl,
    isScheduleEventPublishEnabled,
    schedulePublishDate,
    schedulePublishTime,
    eventTimeZone,
) => {
    let finalMenu = {
        narrowIconType: <ClockFillSvg />,
        narrowIconColor: ORG_ICON_GRAY_COLOR,
        iconColor: 'ui-blue',
        label: HEADER_LABELS.PUBLISH_SCHEDULED_EVENT_LABEL,
        fallbackUrl: serverUrl,
        hideDropIconWhenNarrow: true,
        textItems: [
            {
                content: <ActionText text={HEADER_LABELS.PUBLIC_NOW_LABEL} />,
                value: PUBLISH_EVENT_INTENT,
            },
            {
                content: (
                    <ActionText
                        text={HEADER_LABELS.CANCEL_PUBLISH_TIME_LABEL}
                    />
                ),
                value: CANCEL_SCHEDULE_EVENT_INTENT,
            },
        ],
    };

    if (isScheduleEventPublishEnabled) {
        const schedulePublishTimezone = getFormattedTimezone(eventTimeZone);

        finalMenu = {
            ...finalMenu,
            label: HEADER_LABELS.SCHEDULED_EVENT_LABEL,
            narrowIconType: <CalendarCheckMarkChunkySvg />,
            /*
            Published On and Reschedule (first two items) textListItem are the same, communicated to user differently
            TextList does not allow for duplicate values which is why the values have numbered suffixes
            */
            textItems: [
                {
                    content: (
                        <div>
                            {HEADER_LABELS.PUBLISH_ON_EVENT_LABEL}
                            <p>
                                {gettext(
                                    '%(schedulePublishDate)s at %(schedulePublishTime)s %(schedulePublishTimezone)s',
                                    {
                                        schedulePublishDate,
                                        schedulePublishTime,
                                        schedulePublishTimezone,
                                    },
                                )}
                            </p>
                        </div>
                    ),
                    value: `${SCHEDULE_PUBLISH_EVENT_INTENT}-1`,
                },
                {
                    content: HEADER_LABELS.RESCHEDULE_PUBLISH_EVENT_LABEL,
                    value: `${SCHEDULE_PUBLISH_EVENT_INTENT}-2`,
                },
                {
                    content: HEADER_LABELS.UNSCHEDULE_EVENT_LABEL,
                    value: CANCEL_SCHEDULE_EVENT_INTENT,
                },
                {
                    content: HEADER_LABELS.PUBLIC_NOW_LABEL,
                    value: PUBLISH_EVENT_INTENT,
                },
            ],
        };
    }
    return finalMenu;
};

export const getCanceledEventActionMenu = (serverUrl) => ({
    iconType: <StatusDotChunkySvg />,
    iconColor: 'ui-red',
    narrowIconType: <CalendarOffChunkySvg />,
    narrowIconColor: ORG_ICON_GRAY_COLOR,
    label: HEADER_LABELS.CANCELED_EVENT_LABEL,
    fallbackUrl: serverUrl,

    textItems: [
        {
            content: HEADER_LABELS.CANCELED_LABEL,
            value: 'cancel',
        },
    ],
});

export const getEventStatusMenuItem = ({
    serverUrl,
    eventSalesStatus,
    locale,
}) => {
    const {
        eventStatusLabel,
        eventStatusNarrowIconType,
        eventStatusNarrowIconColor,
        eventStatusIconColor,
    } = getLabelAndIconToDisplay(eventSalesStatus, locale);

    const statusItem = {
        iconType: <StatusDotChunkySvg />,
        iconColor: eventStatusIconColor,
        narrowIconColor: eventStatusNarrowIconColor,
        fallbackUrl: serverUrl,
        narrowIconType: eventStatusNarrowIconType,
        label: eventStatusLabel,
        textItems: [
            {
                content: eventStatusLabel,
                value: '',
            },
        ],
    };

    return statusItem;
};

export const getEndedEventActionMenu = ({
    serverUrl,
    eventSalesStatus,
    locale,
}) => {
    const newEventStatusLabelIcons = getLabelAndIconToDisplay(
        eventSalesStatus,
        locale,
    );
    const {
        eventStatusLabel = HEADER_LABELS.ENDED_EVENT_LABEL,
        eventStatusNarrowIconType = <CalendarOffChunkySvg />,
        eventStatusNarrowIconColor = ORG_ICON_GRAY_COLOR,
        eventStatusIconColor = ORG_ICON_GRAY_COLOR,
    } = newEventStatusLabelIcons;
    let navItems;

    if (isMediumDown()) {
        navItems = [
            {
                content: (
                    <ActionText
                        text={eventStatusLabel}
                        className="eds-text-color--ui-500"
                    />
                ),
                type: ANCHOR_LINK,
                isDisabled: true,
            },
        ];
    }

    let actionMenu = {
        iconType: <StatusDotChunkySvg />,
        iconColor: eventStatusIconColor,
        narrowIconColor: eventStatusNarrowIconColor,
        fallbackUrl: serverUrl,
        narrowIconType: eventStatusNarrowIconType,
        label: eventStatusLabel,
    };

    if (navItems) {
        actionMenu = {
            navItems,
            ...actionMenu,
        };
    } else {
        actionMenu = {
            textItems: [
                {
                    content: eventStatusLabel,
                    value: 'ended',
                },
            ],
            ...actionMenu,
        };
    }

    return actionMenu;
};

export const getPrimaryActionFromErrorDetail = (
    navigate,
    errorDetail,
    baseUrl,
    eventId,
) => {
    let primaryAction;

    if (errorDetail.getPrimaryActionLink) {
        const url = errorDetail.getPrimaryActionLink(baseUrl, eventId);

        primaryAction = {
            primaryText:
                errorDetail.primaryActionText || HEADER_LABELS.VIEW_LABEL,
            primaryType: 'link',
            onPrimaryAction: () => {
                if (errorDetail.isActionLinkExternal) {
                    window.location.href = url;
                } else {
                    navigate(url);
                }
            },
        };
    }

    return primaryAction;
};

export const getSecondaryActionFromErrorDetail = (
    navigate,
    errorDetail,
    baseUrl,
    eventId,
) => {
    let secondaryAction;

    if (errorDetail.getSecondaryActionLink) {
        const url = errorDetail.getSecondaryActionLink(baseUrl, eventId);

        secondaryAction = {
            secondaryText:
                errorDetail.secondaryActionText || HEADER_LABELS.VIEW_LABEL,
            secondaryType: 'link',
            onSecondaryAction: () => {
                if (errorDetail.isActionLinkExternal) {
                    window.location.href = url;
                } else {
                    navigate(url);
                }
            },
        };
    }

    return secondaryAction;
};

export const getUserMenuInfo = ({
    env,
    user,
    organization = { name: undefined },
    hideOrganizationSwitch = false,
    onlyUseAnchorLinks,
}) => {
    const { serverUrl, adminServerUrl, logoutUrl, requestPath } = env;
    const {
        publicId: publicUserId,
        fullName,
        email,
        thumbnailUrl,
        isStaff,
        hasTikibar,
        numOrders,
        numSavedEvents,
        numFollowing,
        firstName,
    } = user;

    if (user.usesNewPermissions) {
        return {
            ...getOrganizationUserMenuInfo({
                showEventsLink: true,
                organizationName: organization.name,
                organizationImageUrl: thumbnailUrl,
                hideOrganizationSwitch,
                publicUserId,
                requestPath,
                serverUrl,
                email,
                fullName,
                userImageUrl: thumbnailUrl,
                isStaff,
                adminServerUrl,
                hasTikibar,
                logoutUrl,
                numOrders,
                numSavedEvents,
                numFollowing,
                onlyUseAnchorLinks,
            }),
            iconColor: ORG_ICON_GRAY_COLOR,
            label: firstName,
        };
    }

    return getDefaultUserMenu({
        serverUrl,
        adminServerUrl,
        logoutUrl,
        requestPath,
        user,
        iconColor: ORG_ICON_GRAY_COLOR,
    });
};
